import { http } from '../request'
import { fetchAllPages, type PageResponse } from '@/utils/pagination'
import {
  type BulkActionResult,
  type ManagementEntity,
  type ManagementImportResult,
  normalizeListResponse,
  normalizePageResponse,
  normalizeStatusText
} from './shared'

export interface SubgroupSummary {
  id: number
  name: string
  studentCount: number
}

export interface ClassDto extends ManagementEntity {
  id: number
  name: string
  baseName?: string
  gradeName: string
  gradeFullName?: string
  stageName?: string
  gradeId: number
  gradeLevel?: number
  /** 年级全局顺序号（后端系统维护，用于排序） */
  gradeOrder?: number
  campusId?: number
  campusName?: string
  year?: number
  headTeacherId?: number
  headTeacherName: string
  classroomId?: number
  classroomName?: string
  studentCount: number
  capacity?: number
  subgroupNames?: string[]
  subgroups?: SubgroupSummary[]
  hasActiveTimetable?: boolean
  pendingName?: string
  pendingEffectiveDate?: string
}

export interface ClassSubgroupDto {
  id: number
  classId: number
  name: string
  studentCount: number
}

type ClassPayload = {
  name: string
  gradeId: number | undefined
  campusId: number | undefined
  classroomId: number | undefined
  capacity: number | undefined
  status: string
  remark: unknown
  effectiveDate?: string
}

function toClassPayload(payload: unknown): ClassPayload {
  const cls = payload as Partial<ClassDto> & Record<string, unknown>
  const effectiveDate = typeof cls.effectiveDate === 'string' ? cls.effectiveDate.trim() : ''
  return {
    name: String(cls.name ?? '').trim(),
    gradeId: cls.gradeId != null ? Number(cls.gradeId) : undefined,
    campusId: cls.campusId != null ? Number(cls.campusId) : undefined,
    classroomId: cls.classroomId != null ? Number(cls.classroomId) : undefined,
    capacity: cls.capacity != null ? Number(cls.capacity) : undefined,
    status: normalizeStatusText(cls.status),
    remark: cls.remark,
    ...(effectiveDate ? { effectiveDate } : {})
  }
}

async function listAllClasses(status?: string): Promise<ClassDto[]> {
  return fetchAllPages<ClassDto>({
    fetchFn: (params) => http.get<PageResponse<ClassDto>>(
      '/data-management/classes',
      status ? { ...params, status } : params
    ),
    normalizeFn: (raw) => normalizePageResponse<ClassDto>(raw)
  })
}

export const classApi = {
  /**
   * 班级列表。缺省返回全量（含已归档，供回读/管理场景）；
   * 操作场景（新排课等）请传 `status: '启用'` 隐藏归档组织。
   */
  async list(status?: '启用' | '归档'): Promise<ClassDto[]> {
    return listAllClasses(status)
  },
  async create(payload: unknown): Promise<ClassDto> {
    const response = await http.post<ClassDto>('/data-management/classes', toClassPayload(payload))
    return response.data
  },
  async update(id: number, payload: unknown): Promise<ClassDto> {
    const response = await http.put<ClassDto>(`/data-management/classes/${id}`, toClassPayload(payload))
    return response.data
  },
  async remove(id: number): Promise<void> {
    await http.delete<void>(`/data-management/classes/${id}`)
  },
  async batchRemove(ids: number[]): Promise<BulkActionResult> {
    const response = await http.post<BulkActionResult>('/data-management/classes/batch-delete', { ids })
    return response.data
  },
  async batchUpdateStatus(ids: number[], status: string): Promise<number> {
    const response = await http.post<number>('/data-management/classes/batch-update-status', { ids, status })
    return response.data
  },
  async importRows(rows: unknown[]): Promise<ManagementImportResult> {
    const response = await http.post<ManagementImportResult>(
      '/data-management/classes/import',
      rows.map(toClassPayload)
    )
    return response.data
  },
  async listSubgroups(classId: number): Promise<ClassSubgroupDto[]> {
    const response = await http.get<ClassSubgroupDto[]>(
      `/data-management/classes/${classId}/subgroups`
    )
    return normalizeListResponse<ClassSubgroupDto>(response.data)
  },
  async createSubgroup(classId: number, payload: { name: string }): Promise<ClassSubgroupDto> {
    const response = await http.post<ClassSubgroupDto>(
      `/data-management/classes/${classId}/subgroups`,
      payload
    )
    return response.data
  },
  async updateSubgroup(
    classId: number,
    subgroupId: number,
    payload: { name: string; effectiveDate?: string }
  ): Promise<ClassSubgroupDto> {
    const response = await http.put<ClassSubgroupDto>(
      `/data-management/classes/${classId}/subgroups/${subgroupId}`,
      payload
    )
    return response.data
  },
  async removeSubgroup(classId: number, subgroupId: number): Promise<void> {
    await http.delete<void>(`/data-management/classes/${classId}/subgroups/${subgroupId}`)
  },
  async archiveSubgroup(classId: number, subgroupId: number): Promise<ClassSubgroupDto> {
    const response = await http.post<ClassSubgroupDto>(
      `/data-management/classes/${classId}/subgroups/${subgroupId}/archive`
    )
    return response.data
  },
  async archiveEnabledSubgroups(classId: number): Promise<number> {
    const response = await http.post<number>(
      `/data-management/classes/${classId}/subgroups/archive-enabled`
    )
    return Number(response.data ?? 0)
  },
  async countUnassignedStudents(classId: number): Promise<number> {
    const response = await http.get<number>(
      `/data-management/classes/${classId}/subgroups/unassigned-count`
    )
    return Number(response.data ?? 0)
  },
  async assignUnassignedStudents(classId: number, strategy: string, targetSubgroupId?: number): Promise<number> {
    const response = await http.post<number>(
      `/data-management/classes/${classId}/subgroups/assign-unassigned`,
      { strategy, targetSubgroupId: targetSubgroupId ?? null }
    )
    return Number(response.data ?? 0)
  }
}
